/**
 * Onglet « Données reçues » d'un compteur électrique (PDL) — logique PURE,
 * extraite du composant pour être testable (DEV-1496 ; patron identique à
 * `meter-technical-data-pdl.helper.ts`).
 *
 * ## Ce que cet onglet EST
 *
 * Un affichage de **STOCK** : « qu'est-ce qu'on détient, de quand à quand ».
 * Ce n'est PAS l'état du process de récupération (qui a sa bannière et son objet
 * dédié), ni la fraîcheur (« de quand date la dernière mesure »). La règle du
 * 25/08 est que **l'affiché corresponde à ce qu'il y a en base**, servi par dapi
 * `/source/informations`.
 *
 * ## Les quatre défauts corrigés
 *
 * 1. **La courbe de charge lisait le mauvais grain.** La ligne « Courbe de
 *    charge » était configurée sur `energie_elec_tout.hourly`, qui est
 *    l'AGRÉGAT HORAIRE. La courbe de charge canonique vit dans le bloc dédié
 *    `data.soutirage.cdc` / `data.injection.cdc` (avec son `pas`). Écart mesuré
 *    le 25/08 : −15 j sur un témoin C5, −3 j sur un témoin C2 — 15 jours de
 *    données présentes en base annoncées absentes.
 *
 * 2. **Le pas de temps était une constante par segment** (« 30 minutes » en C5,
 *    « 10 minutes » en C2) alors que dapi sert le pas réel dans `cdc.pas` :
 *    « 10 minutes » affiché sur un compteur à `5 min`.
 *
 * 3. **Badge et dates ne lisaient pas le même endroit** de la charge utile dans
 *    le même encadré (badge sur `cdc`, dates sur `…tout.hourly`). Ils sont
 *    désormais alignés sur la même source par ligne.
 *
 * 4. **Des « Erreur de réception » étaient FABRIQUÉES.** Depuis DEV-1506, dapi
 *    n'expose plus, pour une source Enedis, que les bornes dont l'écrivain est
 *    VIVANT (`energie_elec_tout`, `energie_elecinjection_tout`) : les 147 autres
 *    chemins étaient figés au 16-17/08 et servis comme vivants. La PMax
 *    (`puissance_elec_tout`) et la réactive (`energie_elec*_reactive`) ne sont
 *    donc plus dans `_variables` DU TOUT. Le composant lisait cette absence de
 *    BORNE comme une absence de DONNÉE et badgeait « Erreur de réception » : une
 *    erreur affirmée sur un chemin où rien n'a échoué. On distingue désormais
 *    « pas de dates publiées » (informatif) de « rien reçu » (défaut) — même
 *    patron que le gaz, où l'absence de publié M023 est informative et non une
 *    erreur.
 *
 *    ⚠️ **Et ce motif se lit sur la LISTE BLANCHE, jamais sur l'absence runtime
 *    de la variable dans `_variables`** (revue du 31/08). Cette absence a deux
 *    causes OPPOSÉES : « dapi ne publie pas de bornes pour ce chemin » et « ce
 *    compteur n'a jamais rien reçu ». Un PDL qui n'a jamais rien reçu a un
 *    `_variables` VIDE : l'aiguillage par l'absence faisait donc basculer TOUTES
 *    ses lignes en « Dates non publiées » (canal `info`), avec un tooltip qui
 *    affirmait « ces mesures sont collectées et consultables dans les
 *    graphiques ». Le défaut devenait rassurant, et la contre-vérité était
 *    servie au client précisément quand rien ne marchait.
 *
 * 5. **La ligne réactive était badgée d'après la CDC ACTIVE.** L'aiguillage se
 *    faisait sur `data.name === "Courbe de charge"`, vrai aussi pour les lignes
 *    réactives : leur badge recopiait `has_soutirage_cdc`, c'est-à-dire l'état
 *    d'une AUTRE grandeur. L'aiguillage se fait maintenant sur la VARIABLE.
 *
 * ## Ce que le badge a le droit d'affirmer
 *
 * `data_received` dit « nous détenons ces données » — un fait de stock, lisible
 * sur `date_from`/`date_to`. Il ne dit pas « à jour » : la fraîcheur ne se juge
 * pas sur une borne de début, et elle a son propre canal (la bannière, qui porte
 * le verdict et l'attendu du compteur).
 */
import { DataVariable, InformationPDL, LineBlock, LineBlockBadge } from "@types"

/** Une ligne de l'onglet, telle que déclarée dans les messages i18n du composant. */
export type ReceivedDataTab = {
    title: string[]
    data: {
        name: string
        variable: string
        freq: string
        freqName: string
    }
}

/** Sens de mesure sélectionné par le bouton soutirage / injection. */
export type SensPdl = "soutirage" | "injection"

type Translate = (key: string) => string

type Slot = { date_from?: Date | string | null; date_to?: Date | string | null }

/**
 * Variables d'énergie ACTIVE dont la courbe de charge canonique vit dans le bloc
 * `data.<sens>.cdc`. L'aiguillage se fait sur la variable (jamais sur le libellé
 * `data.name`, qui vaut aussi « Courbe de charge » pour les lignes réactives).
 */
const VARIABLES_CDC_ACTIVE: Record<string, SensPdl> = {
    energie_elec_tout: "soutirage",
    energie_elecinjection_tout: "injection",
}

/**
 * ⚠️ **MIROIR MANUEL** de la liste blanche dapi `BORNES_VIVANTES_ENEDIS`
 * (`modules/source/get_information_source.py`), ÉPINGLÉE au sha dapi
 * `208975a358bf9d2982be85574b68b5196bd05f73` (`origin/main`, lu le 31/08/2026).
 *
 * **Aucune synchronisation automatique.** dapi qui rallume un chemin (option A
 * de DEV-1506) ne met PAS ce fichier à jour, et ce fichier ne contraint pas dapi.
 * Les deux dérives possibles :
 * - dapi rallume un chemin absent d'ici ⇒ le front badge « Dates non publiées »
 *   une variable redevenue vivante, et masque un vrai défaut de réception ;
 * - dapi éteint un chemin encore listé ici ⇒ le front fabrique à nouveau une
 *   « Erreur de réception » sur un chemin où rien n'a échoué.
 *
 * Tout mouvement de `BORNES_VIVANTES_ENEDIS` doit donc arriver ici **dans la
 * même livraison**. Le SSOT reste dapi ; ceci n'en est qu'une copie datée.
 *
 * Clé = variable, valeur = grains dont les bornes sont SERVIES. Le grain compte
 * autant que la variable : dapi sert `energie_elec_tout.hourly` avec des bornes
 * nulles (grain figé hors `{daily, raw}`), ce qui n'est pas un défaut non plus.
 *
 * `min_freq` / `max_freq` (bornes de facturation) sont dans la liste dapi mais
 * volontairement absents d'ici : `get_list_variables` ne les énumère jamais
 * (noms en 2 parties, exclus par la règle « 3 parties séparées par `_` »), et le
 * bloc facturation est lu par son chemin dédié plus bas.
 */
const BORNES_SERVIES_ENEDIS: Record<string, ReadonlySet<string>> = {
    energie_elec_tout: new Set(["daily", "raw"]),
    energie_elecinjection_tout: new Set(["daily", "raw"]),
}

/**
 * dapi publie-t-il des bornes pour ce (variable, grain) ?
 *
 * Se lit sur la liste blanche ENCODÉE, jamais sur ce que le compteur a reçu :
 * c'est une propriété de l'EXPOSITION dapi, identique pour tout le parc.
 * @param tab - Ligne de l'onglet
 * @returns `true` si l'absence de dates doit se lire comme un DÉFAUT
 */
function bornesPubliees(tab: ReceivedDataTab): boolean {
    return (
        BORNES_SERVIES_ENEDIS[tab?.data?.variable]?.has(tab?.data?.freq) ??
        false
    )
}

/**
 * La ligne est-elle LA courbe de charge active du sens affiché ?
 * @param tab - Ligne de l'onglet
 * @param sens - Sens sélectionné
 * @returns `true` si la ligne doit lire le bloc `cdc` dédié
 */
function estCourbeDeChargeActive(tab: ReceivedDataTab, sens: SensPdl): boolean {
    return (
        VARIABLES_CDC_ACTIVE[tab?.data?.variable] === sens &&
        tab?.data?.freq === "hourly"
    )
}

/**
 * Formate une borne pour l'affichage.
 * @param valeur - Date servie par dapi
 * @param formatDate - Formateur du composant
 * @returns Date formatée, ou `-`
 */
function borne(
    valeur: Date | string | null | undefined,
    formatDate: (d: Date | string) => string
): string {
    return valeur ? formatDate(valeur) : "-"
}

/** Badge « nous détenons ces données » — un fait de STOCK, pas de fraîcheur. */
function badgeRecues(t: Translate): LineBlockBadge {
    return {
        text: t("data_received"),
        icon: "pi pi-wifi",
        status: "success",
    }
}

/** Badge « rien reçu » — un vrai défaut, sur un chemin dont la borne est servie. */
function badgeDefaut(t: Translate): LineBlockBadge {
    return {
        text: t("error_data"),
        icon: "pi pi-times",
        status: "warn",
    }
}

/**
 * Badge « nous ne publions pas de dates pour cette ligne » (DEV-1506).
 *
 * Informatif, JAMAIS une erreur : la donnée est collectée et consultable dans
 * les graphiques ; c'est la plage de dates qui n'est pas mesurée de façon
 * fiable, et dapi a choisi de ne rien servir plutôt que de servir faux.
 */
function badgeSansBornes(t: Translate): LineBlockBadge {
    return {
        text: t("no_bounds_published"),
        icon: "pi pi-info-circle",
        status: "info",
        tooltip: t("no_bounds_published_tooltip"),
    }
}

/**
 * Construit les blocs de l'onglet « Données reçues » d'un PDL.
 *
 * @param pdl - `informations.pdl` de la source
 * @param tabs - Lignes déclarées pour le segment × sens (messages i18n)
 * @param sens - Sens sélectionné (soutirage / injection)
 * @param t - Traducteur du composant
 * @param formatDate - Formateur de date du composant
 * @param billingTitle - Titre du bloc facturation (`tm("billing_pdl.title")`)
 * @returns Blocs prêts à rendre
 */
export function buildPdlReceivedDataBlocks(
    pdl: InformationPDL | null | undefined,
    tabs: ReceivedDataTab[],
    sens: SensPdl,
    t: Translate,
    formatDate: (d: Date | string) => string,
    billingTitle: string[] = []
): LineBlock[] {
    if (!pdl || !Array.isArray(tabs)) return []

    const cdc =
        sens === "soutirage"
            ? pdl?.data?.soutirage?.cdc
            : pdl?.data?.injection?.cdc
    const cdcExiste = !!(
        (sens === "soutirage"
            ? pdl?.data?.soutirage?.cdc?.has_soutirage_cdc
            : pdl?.data?.injection?.cdc?.has_injection_cdc) || cdc?.date_from
    )
    const blocs: LineBlock[] = tabs.map((tab) => {
        // ---- La courbe de charge lit SON bloc, dates ET pas (défauts 1-2-3) ---
        if (estCourbeDeChargeActive(tab, sens)) {
            // ⚠️ Une 3ᵉ branche « CDC en cours d'activation » vivait ici, sur
            // `data_retrieval.historical.state === "delayed"`. Elle est RETIRÉE :
            // depuis DEV-1324, ce champ est écrit par le seul
            // `_etat_historical()` de dapi (`modules/source/data_retrieval_enedis.py`,
            // sha 208975a3 lu le 31/08/2026), qui ne rend que
            // `noconsent | finished | crashed | waiting` — ou `null` en
            // dégradation. `"delayed"` ne survit que dans l'énum pydantic
            // `StateWorkflowPdl` (legacy documentaire) : AUCUN écrivain ne
            // l'émet, la branche était donc morte et son test vert n'attestait
            // rien. Elle reviendra avec le chemin qui l'alimente, pas avant.
            const badge: LineBlockBadge = cdcExiste
                ? badgeRecues(t)
                : badgeDefaut(t)

            return {
                title: tab?.title,
                badge: [badge],
                lines: [
                    {
                        // `pas` SERVI par Enedis ; le libellé du segment n'est
                        // qu'un repli quand dapi ne le porte pas.
                        text: t("time_step"),
                        value: cdc?.pas || tab?.data?.freqName,
                    },
                    {
                        text: t("date_first_data"),
                        value: borne(cdc?.date_from, formatDate),
                    },
                    {
                        text: t("date_last_data"),
                        value: borne(cdc?.date_to, formatDate),
                    },
                ],
            }
        }

        // ---- Les autres lignes lisent `_variables` -------------------------
        const variables: DataVariable[] = pdl?.data?._variables ?? []
        const variable = variables.find(
            (v) => v?.type === tab?.data?.variable
        ) as unknown as Record<string, Slot> | undefined
        const slot = variable?.[tab?.data?.freq] ?? null

        // Aiguillage sur la LISTE BLANCHE, jamais sur l'absence runtime de la
        // variable (défaut 4). `!variable` confond deux situations opposées :
        // un chemin dont dapi ne publie pas les bornes (informatif, vrai pour
        // TOUT le parc) et un compteur qui n'a JAMAIS rien reçu (`_variables`
        // vide — un vrai défaut, qui doit rester « Erreur de réception »).
        const badge = !bornesPubliees(tab)
            ? badgeSansBornes(t)
            : slot?.date_from
              ? badgeRecues(t)
              : badgeDefaut(t)

        return {
            title: tab?.title,
            badge: [badge],
            lines: [
                { text: t("time_step"), value: tab?.data?.freqName },
                {
                    text: t("date_first_data"),
                    value: borne(slot?.date_from, formatDate),
                },
                {
                    text: t("date_last_data"),
                    value: borne(slot?.date_to, formatDate),
                },
            ],
        }
    })

    // Facturation : bloc dédié servi par dapi, hors `_variables` — soutirage seul.
    if (sens === "soutirage") {
        const facturation = pdl?.data?.soutirage?.facturation
        blocs.unshift({
            title: billingTitle,
            badge: [facturation?.date_from ? badgeRecues(t) : badgeDefaut(t)],
            lines: [
                { text: t("time_step"), value: "-" },
                {
                    text: t("date_first_data"),
                    value: borne(facturation?.date_from, formatDate),
                },
                {
                    text: t("date_last_data"),
                    value: borne(facturation?.date_to, formatDate),
                },
            ],
        })
    }

    return blocs
}
